import React from 'react';
import { Link } from 'react-router-dom';
import LocLink from '../components/LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Heart, Target, Users, CheckCircle, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import { getLocalizedImage, getLocalizedAlt } from '../utils/getLocalizedImage';

const IMAGES = {
  quebec: 'https://images.unsplash.com/photo-1691516094722-3b6662d72140?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxxdWViZWMlMjBsYW5kc2NhcGUlMjBuYXR1cmV8ZW58MHx8fHwxNzc2MzUwMjkyfDA&ixlib=rb-4.1.0&q=85',
  founder: '/images/1uef6ln1_68c7a61c-0ca1-4ea0-9fc7-3e90a4fdd063.png',
  journal: '/images/uo2d5vec_496000699_10163830369496535_3793051834130385264_n_-_Copie.jpg',
  hero: '/images/ksz710nm_iap_300x300.7361607795_f6zng6jf.jpg',
};

const StoryPage = () => {
  const { t, language } = useLanguage();

  const timeline = language === 'fr' ? [
    {
      title: 'Le d\u00e9but',
      description: 'Les m\u00e9dicaments d\u2019urgence sont fragiles, co\u00fbteux, et souvent transport\u00e9s dans des sacs o\u00f9 ils peuvent \u00eatre endommag\u00e9s. Rien sur le march\u00e9 n\u2019\u00e9tait adapt\u00e9. Quand une personne a besoin de son m\u00e9dicament en urgence, la derni\u00e8re chose dont elle devrait se soucier est de savoir s\u2019il a surv\u00e9cu \u00e0 une journ\u00e9e dans un sac.',
    },
    {
      title: 'La solution',
      description: 'Nous avons con\u00e7u et imprim\u00e9 en 3D le premier prototype \u2014 un \u00e9tui sur mesure pour le vaporisateur de nitroglyc\u00e9rine (Rho-Nitro / Nitrolingual).',
    },
    {
      title: 'L\u2019expansion',
      description: 'La demande a grandi. Nous avons d\u00e9velopp\u00e9 des \u00e9tuis pour l\u2019EpiPen, le Valtoco, les kits d\u2019hydrocortisone injectable d\u2019urgence et plus encore.',
    },
    {
      title: 'Aujourd\u2019hui',
      description: 'Plus de 1\u202f200 ventes sur Etsy dans plus de 10 pays \u2014 utilis\u00e9s par des patients, des proches aidants et des familles qui transportent des m\u00e9dicaments d\u2019urgence au quotidien.',
    },
  ] : [
    {
      title: 'The Beginning',
      description: 'Emergency medications are fragile, expensive, and often carried in backpacks, purses, or pockets where they can be damaged. Nothing on the market was suitable. When someone needs their medication in an emergency, the last thing they should worry about is whether it survived the day in a backpack.',
    },
    {
      title: 'The Solution',
      description: 'We designed and 3D-printed the first prototype \u2014 a custom case specifically for nitroglycerin spray (Rho-Nitro / Nitrolingual).',
    },
    {
      title: 'The Expansion',
      description: 'Demand grew. We developed cases for EpiPen, Valtoco, emergency hydrocortisone injection kits, and more.',
    },
    {
      title: 'Today',
      description: 'Over 1,200 Etsy sales across 10+ countries \u2014 used by patients, caregivers, and families who carry emergency medications every day.',
    },
  ];

  const values = language === 'fr' ? [
    {
      icon: Target,
      title: 'Pr\u00e9cision',
      description: 'Chaque \u00e9tui est con\u00e7u pour un m\u00e9dicament sp\u00e9cifique, pas une solution g\u00e9n\u00e9rique.',
    },
    {
      icon: Heart,
      title: 'Empathie',
      description: 'Nous comprenons l\u2019urgence et l\u2019anxi\u00e9t\u00e9 de transporter des m\u00e9dicaments essentiels.',
    },
    {
      icon: Users,
      title: 'Communaut\u00e9',
      description: 'Nos clients sont nos partenaires \u2014 leurs retours fa\u00e7onnent chaque am\u00e9lioration.',
    },
  ] : [
    {
      icon: Target,
      title: 'Precision',
      description: 'Every case is designed for a specific medication, not a generic solution.',
    },
    {
      icon: Heart,
      title: 'Empathy',
      description: 'We understand the urgency and anxiety of carrying essential medications.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Our customers are our partners \u2014 their feedback shapes every improvement.',
    },
  ];

  return (
    <main className="pt-20 sm:pt-24">
      <SEOHead
        routeKey="story"
        language={language}
        title={language === 'fr' ? 'Notre histoire — Né d\u2019une vraie urgence | MyGuardPod' : 'Our Story — Born From a Real Emergency | MyGuardPod'}
        description={language === 'fr'
          ? 'L\u2019histoire de MyGuardPod : née à Baie-Comeau, Québec, d\u2019un besoin concret pour transporter et protéger les médicaments d\u2019urgence.'
          : 'The MyGuardPod story: born in Baie-Comeau, Québec, from a real need to carry and protect emergency medications.'}
      />
      <JsonLd data={breadcrumbSchema([{ name: language === 'fr' ? 'Notre histoire' : 'Our Story', routeKey: 'story' }], language)} />
      {/* Hero */}
      <section className="overflow-hidden banner-fade">
        <img
          src={getLocalizedImage('storyHero', language)}
          alt={getLocalizedAlt('storyHero', language)}
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Story Content */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="font-manrope text-lg text-neutral-600 leading-relaxed space-y-6">
                {t('origin.description').split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={IMAGES.founder}
                alt="Alain Poirier - Fondateur de MyGuardPod avec le NitroPod"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Media / Press */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {language === 'fr' ? 'Dans les m\u00e9dias' : 'In the Media'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={IMAGES.journal}
                alt={language === 'fr' ? 'Article du journal Le Manic sur MyGuardPod' : 'Le Manic newspaper article about MyGuardPod'}
                className="rounded-2xl shadow-lg w-full"
              />
              <p className="font-manrope text-sm text-neutral-500 mt-3 text-center">
                {language === 'fr' ? 'Article paru dans Le Manic' : 'Featured in Le Manic newspaper'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-neutral-50 rounded-2xl p-8">
                <span className="inline-block font-outfit font-bold text-sm uppercase tracking-widest text-[#E31B23] mb-3">
                  {language === 'fr' ? 'Entrevue Radio' : 'Radio Interview'}
                </span>
                <h3 className="font-outfit font-bold text-2xl text-[#1a1a1a] mb-4">
                  {language === 'fr'
                    ? 'MyGuardPod d\u2019Alain Poirier : prot\u00e9ger ce qui prot\u00e8ge la vie'
                    : 'MyGuardPod by Alain Poirier: protecting what protects life'}
                </h3>
                <p className="font-manrope text-neutral-600 mb-6">
                  {language === 'fr'
                    ? 'Entrevue compl\u00e8te sur les origines de MyGuardPod, la conception des \u00e9tuis en impression 3D et la mission de prot\u00e9ger les m\u00e9dicaments d\u2019urgence.'
                    : 'Full interview about MyGuardPod\u2019s origins, 3D-printed case design, and the mission to protect emergency medications.'}
                </p>
                <a
                  href="https://radiolpweb.com/myguardpod-dalain-poirier-proteger-ce-qui-protege-la-vie/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-5 py-3 rounded-full font-bold transition-all duration-300 active:scale-95"
                  data-testid="radio-interview-link"
                >
                  <span>{language === 'fr' ? '\u00c9couter l\u2019entrevue' : 'Listen to the interview'}</span>
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 sm:py-32 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a]">
              {language === 'fr' ? 'Notre parcours' : 'Our Journey'}
            </h2>
          </motion.div>

          <div className="space-y-8">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6"
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#E31B23] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-red-200 mt-4" />
                  )}
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm flex-1">
                  <h3 className="font-outfit font-bold text-xl text-[#1a1a1a] mb-2">
                    {item.title}
                  </h3>
                  <p className="font-manrope text-neutral-600">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {language === 'fr' ? 'Nos valeurs' : 'Our Values'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#E31B23]" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-[#1a1a1a] mb-3">
                  {value.title}
                </h3>
                <p className="font-manrope text-neutral-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quebec Pride */}
      <section className="py-24 sm:py-32 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <span className="text-lg">&#127809;</span>
                <span>Made in Canada</span>
              </div>

              <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-white mb-6">
                {t('quebec.title')}
              </h2>

              <p className="font-manrope text-lg text-neutral-300 leading-relaxed mb-8">
                {t('quebec.description')}
              </p>

              <ul className="space-y-3">
                {t('quebec.features').map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-[#E31B23] flex-shrink-0" />
                    <span className="font-manrope">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={IMAGES.hero}
                alt="MyGuardPod case"
                className="rounded-3xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-8">
              {language === 'fr'
                ? 'Pr\u00eat \u00e0 d\u00e9couvrir nos produits?'
                : 'Ready to explore our products?'}
            </h2>
            <LocLink
              to="products"
              className="inline-flex items-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
              data-testid="story-products-cta"
            >
              <span>{t('hero.cta')}</span>
              <ChevronRight className="w-5 h-5" />
            </LocLink>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default StoryPage;
